<template>
  <div class="shift-management">
    <div class="page-title">班次管理</div>
    
    <!-- 搜索筛选区域 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-input placeholder="搜索班次名称" v-model="searchKeyword" class="filter-input" @keyup.enter.native="handleSearch">
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>
        <el-button type="primary" plain @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
        <div class="spacer"></div>
        <el-button type="primary" icon="el-icon-plus" @click="openAddDialog">新增班次</el-button>
      </div>
    </div>

    <el-table
      :data="tableData"
      border
      style="width: 100%"
      v-loading="loading">
      <el-table-column prop="name" label="班次名称" min-width="150"></el-table-column>
      <el-table-column label="开始时间" min-width="120">
        <template slot-scope="scope">
          {{ scope.row.startTime || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="结束时间" min-width="120">
        <template slot-scope="scope">
          {{ scope.row.endTime || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="创建人" min-width="120">
        <template slot-scope="scope">
          {{ scope.row.createUserName || scope.row.createBy || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="创建时间" min-width="180">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="最后修改人" min-width="120">
        <template slot-scope="scope">
          {{ scope.row.updateUserName || scope.row.updateBy || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="最后修改时间" min-width="180">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.updateTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 新增/编辑班次弹窗 -->
    <el-dialog
      :title="dialogType === 'add' ? '新增班次' : '编辑班次'"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false">
      
      <div class="dialog-content">
        <div class="form-item">
          <div class="form-label required">班次名称</div>
          <el-input 
            v-model="formData.name" 
            placeholder="请输入班次名称"
            class="form-input">
          </el-input>
        </div>
        
        <div class="form-item">
          <div class="form-label required">开始时间</div>
          <el-time-select
            v-model="formData.startTime"
            placeholder="选择开始时间"
            :picker-options="{
              start: '00:00',
              step: '00:30',
              end: '23:30'
            }"
            class="form-input">
          </el-time-select>
        </div>
        
        <div class="form-item">
          <div class="form-label required">结束时间</div>
          <el-time-select
            v-model="formData.endTime"
            placeholder="选择结束时间"
            :picker-options="{
              start: '00:00',
              step: '00:30',
              end: '23:30'
            }"
            class="form-input">
          </el-time-select>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { searchShifts, getAllShifts, addShift, updateShift, deleteShift } from '@/api/shift'

export default {
  name: 'ShiftManagement',
  data() {
    return {
      searchKeyword: '',
      tableData: [], // 表格显示的数据
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      
      // 弹窗相关数据
      dialogVisible: false,
      dialogType: 'add', // 'add' 或 'edit'
      formData: {
        id: null,
        name: '',
        startTime: '',
        endTime: ''
      },
      submitLoading: false
    };
  },
  created() {
    this.fetchShiftList();
  },
  methods: {
    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp; // 如果不是有效日期，则返回原值
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    // 获取班次列表
    fetchShiftList() {
      this.loading = true;
      
      // 始终使用分页查询接口
      this.fetchShiftWithPagination();
    },
    
    // 分页查询班次
    fetchShiftWithPagination() {
      // 准备查询参数
      const params = {};
      if (this.searchKeyword && this.searchKeyword.trim() !== '') {
        params.name = this.searchKeyword.trim();
      }
      
      // 添加分页参数
      params.pageNum = this.currentPage;
      params.pageSize = this.pageSize;
      
      console.log('发送分页查询班次请求，参数:', JSON.stringify(params));
      
      searchShifts(params)
        .then(response => {
          console.log('分页查询班次响应:', response);
          if (response && response.code === 200) {
            // 检查响应数据结构
            let dataList = [];
            
            if (response.data && Array.isArray(response.data.list)) {
              // 新的响应结构
              dataList = response.data.list || [];
              this.total = response.data.total || 0;
            } else {
              // 旧的响应结构
              dataList = response.data || [];
              this.total = response.total || dataList.length;
            }
            
            this.tableData = this.processShiftData(dataList);
          } else {
            this.$message.error((response && response.msg) || '获取班次列表失败');
            this.tableData = [];
            this.total = 0;
          }
        })
        .catch(error => {
          console.error('获取班次列表失败:', error);
          // 不在这里显示错误消息，避免重复提示
          this.tableData = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 处理班次数据
    processShiftData(dataList) {
      return dataList.map(item => {
        // 如果没有createUserName但有createUserId和createUserName字段，则组合它们
        if (!item.createUserName && item.createUserId) {
          item.createUserName = item.createUserName || '';
        }
        
        // 如果没有updateUserName但有updateUserId和updateUserName字段，则组合它们
        if (!item.updateUserName && item.updateUserId) {
          item.updateUserName = item.updateUserName || '';
        }
        
        return item;
      });
    },
    
    // 打开新增对话框
    openAddDialog() {
      this.dialogType = 'add';
      this.resetForm();
      // 确保时间字段为空
      this.formData.startTime = '';
      this.formData.endTime = '';
      this.dialogVisible = true;
    },
    
    // 打开编辑对话框
    handleEdit(row) {
      this.dialogType = 'edit';
      this.formData = {
        id: row.id,
        name: row.name,
        startTime: row.startTime,
        endTime: row.endTime
      };
      this.dialogVisible = true;
    },
    
    // 重置表单
    resetForm() {
      this.formData = {
        id: null,
        name: '',
        startTime: '',
        endTime: ''
      };
    },
    
    // 提交表单
    submitForm() {
      // 表单验证
      if (!this.formData.name) {
        this.$message.warning('请输入班次名称');
        return;
      }
      
      if (!this.formData.startTime) {
        this.$message.warning('请选择开始时间');
        return;
      }
      
      if (!this.formData.endTime) {
        this.$message.warning('请选择结束时间');
        return;
      }
      
      this.submitLoading = true;
      
      // 准备提交的数据
      const submitData = {
        name: this.formData.name.trim(),
        startTime: this.formData.startTime,
        endTime: this.formData.endTime
      };
      
      console.log('提交数据:', JSON.stringify(submitData));
      
      if (this.dialogType === 'add') {
        // 新增班次
        addShift(submitData)
          .then(response => {
            console.log('新增班次响应:', response);
            if (response && response.code === 200) {
              this.$message.success('新增班次成功');
              this.dialogVisible = false;
              this.fetchShiftWithPagination(); // 使用分页查询重新获取列表
            } else {
              this.$message.error((response && response.msg) || '新增班次失败');
            }
          })
          .catch(error => {
            console.error('新增班次失败:', error);
            // 不在这里显示错误消息，避免重复提示
          })
          .finally(() => {
            this.submitLoading = false;
          });
      } else {
        // 编辑班次
        updateShift(this.formData.id, submitData)
          .then(response => {
            console.log('编辑班次响应:', response);
            if (response && response.code === 200) {
              this.$message.success('编辑班次成功');
              this.dialogVisible = false;
              this.fetchShiftWithPagination(); // 使用分页查询重新获取列表
            } else {
              this.$message.error((response && response.msg) || '编辑班次失败');
            }
          })
          .catch(error => {
            console.error('编辑班次失败:', error);
            // 不在这里显示错误消息，避免重复提示
          })
          .finally(() => {
            this.submitLoading = false;
          });
      }
    },
    
    // 处理搜索
    handleSearch() {
      this.currentPage = 1; // 重置到第一页
      this.fetchShiftWithPagination();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchKeyword = '';
      this.currentPage = 1;
      this.fetchShiftWithPagination();
    },
    
    // 删除班次
    handleDelete(row) {
      this.$confirm('确认删除该班次吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        deleteShift(row.id)
          .then(response => {
            if (response && response.code === 200) {
              this.$message.success('删除成功');
              this.fetchShiftWithPagination(); // 使用分页查询重新获取列表
            } else {
              this.$message.error((response && response.msg) || '删除失败');
            }
          })
          .catch(error => {
            console.error('删除失败:', error);
            // 不在这里显示错误消息，避免重复提示
          })
          .finally(() => {
            this.loading = false;
          });
      }).catch(() => {
        // 取消删除
      });
    },
    
    // 处理页码变化
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchShiftWithPagination();
    },
    
    // 处理每页显示数量变化
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1; // 重置到第一页
      this.fetchShiftWithPagination();
    },

    // 处理时间选择器聚焦事件
    handleTimeFocus() {
      // 这里可以添加处理逻辑，例如禁用自动选择当前时间
    }
  }
};
</script>

<style scoped>
.shift-management {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

/* 筛选区域样式 */
.filter-section {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
}

.filter-input {
  width: 300px;
}

.spacer {
  flex-grow: 1;
}

.delete-btn {
  color: #f56c6c;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 弹窗样式 */
.dialog-content {
  padding: 0 20px;
}

.form-item {
  margin-bottom: 20px;
}

.form-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;
}

.form-label.required:before {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.form-input {
  width: 100%;
}
</style> 